import { useEffect, useMemo, useState } from 'react';
import { getCzechSuklRsAtcGroups, getCzechSuklRsAtcGroupsQueryKey } from '~/libs/infrastructure/api/availability';
import { queryClient } from '~/infrastructure/connectors/ApiConnector';

import { EXPORT_LIMIT } from '../constants';
import { FiltersValue } from '../components/Filters';
import { buildAvailabilityParams } from '../utils/availabilityQueryParams';
import { useAvailabilityExport } from '../adapters/useAvailabilityExport';

interface UseAvailabilityExportSelectionArgs {
	filters: FiltersValue;
	total: number;
}

function buildSelectionFilterKey(filters: FiltersValue): string {
	return `${filters.items.map((item) => item.id).join(',')}|${filters.rs.join(',')}`;
}

// Encapsulates the export business rules:
// - No filter + more than EXPORT_LIMIT records => export is blocked.
// - Active filter + more than EXPORT_LIMIT records => user must pick records to export.
// - Otherwise the current (filtered) result set is exported directly.
export function useAvailabilityExportSelection({ filters, total }: UseAvailabilityExportSelectionArgs) {
	const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
	const { triggerExport, isExporting } = useAvailabilityExport();
	const selectionFilterKey = useMemo(() => buildSelectionFilterKey(filters), [filters]);

	const hasActiveFilter = filters.items.length > 0 || filters.rs.length > 0;
	const exceedsLimitWithoutFilter = !hasActiveFilter && total > EXPORT_LIMIT;
	const showSelection = hasActiveFilter && total > EXPORT_LIMIT;

	useEffect(() => {
		if (!showSelection) {
			setSelectedIds(new Set());
			return;
		}

		let cancelled = false;
		const params = buildAvailabilityParams(filters, 1, EXPORT_LIMIT);

		queryClient
			.fetchQuery({
				queryKey: getCzechSuklRsAtcGroupsQueryKey(params),
				queryFn: () => getCzechSuklRsAtcGroups(params),
			})
			.then((response) => {
				if (cancelled) {
					return;
				}

				setSelectedIds(new Set(response.items.map((group) => group.id)));
			})
			.catch(() => {
				if (!cancelled) {
					setSelectedIds(new Set());
				}
			});

		return () => {
			cancelled = true;
		};
	}, [showSelection, selectionFilterKey, filters]);

	const clearSelection = () => setSelectedIds(new Set());

	const toggleSelect = (groupId: string, checked: boolean) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (checked) {
				if (next.size < EXPORT_LIMIT) {
					next.add(groupId);
				}
			} else {
				next.delete(groupId);
			}
			return next;
		});
	};

	const handleExport = async () => {
		if (exceedsLimitWithoutFilter) {
			return;
		}

		if (showSelection) {
			if (selectedIds.size > 0) {
				await triggerExport(Array.from(selectedIds));
			}
			return;
		}

		const params = buildAvailabilityParams(filters, 1, EXPORT_LIMIT);
		const response = await queryClient.fetchQuery({
			queryKey: getCzechSuklRsAtcGroupsQueryKey(params),
			queryFn: () => getCzechSuklRsAtcGroups(params),
		});
		const allIds = response.items.map((group) => group.id);

		if (allIds.length > 0) {
			await triggerExport(allIds);
		}
	};

	return {
		selectedIds,
		toggleSelect,
		clearSelection,
		handleExport,
		isExporting,
		hasActiveFilter,
		exceedsLimitWithoutFilter,
		showSelection,
	};
}
